import { useState, useCallback, useEffect, useRef } from 'react';
import { Button, Textarea, Input } from '@monksflow/monks-ui';
import { CheckIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { EditableTextProps } from './EditableText.types';

/**
 * Reusable inline editable text with:
 * - Click-to-edit (ignores clicks during text selection)
 * - Local draft state
 * - Save / Discard actions
 * - Click-outside to cancel
 */
export const EditableText = ({
  value,
  onSave,
  onCancel,
  multiline = false,
  disabled = false,
  renderDisplay,
}: EditableTextProps) => {
  const { t } = useTranslation('contentTest');

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Keep draft in sync when parent value changes and we're not editing
  useEffect(() => {
    if (!isEditing) {
      setDraft(value);
    }
  }, [value, isEditing]);

  const handleCancel = useCallback(() => {
    setDraft(value);
    setIsEditing(false);
    onCancel?.();
  }, [value, onCancel]);

  const handleStartEditing = useCallback(() => {
    if (disabled) return;

    if (typeof window !== 'undefined') {
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) {
        // User is selecting text -> don't enter edit mode
        return;
      }
    }

    setDraft(value);
    setIsEditing(true);
  }, [value, disabled]);

  const handleConfirm = useCallback(() => {
    onSave(draft);
    setIsEditing(false);
  }, [draft, onSave]);

  // Click outside to close (acts like cancel)
  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target as Node)) {
        handleCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing, handleCancel]);

  if (!isEditing) {
    return (
      <div
        onClick={handleStartEditing}
        // Enter mirrors the click; the same guard keeps a disabled field inert.
        onKeyDown={(event) => {
          if (event.key === 'Enter') handleStartEditing();
        }}
        role={disabled ? undefined : 'button'}
        tabIndex={disabled ? undefined : 0}
        className={disabled ? 'cursor-default' : 'cursor-text'}
      >
        {renderDisplay ? renderDisplay(value) : value}
      </div>
    );
  }

  const fieldClass = 'mt-1 w-full shadow-[0px_2px_8px_0px_#8A8A8E4D_inset]';

  return (
    <div ref={rootRef}>
      {multiline ? (
        <Textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          className={fieldClass}
        />
      ) : (
        <Input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          className={fieldClass}
        />
      )}
      <div className="mt-2 flex justify-end gap-1">
        <Button
          variant="outline"
          size="icon"
          onClick={handleConfirm}
          aria-label={t('copyWidget.editableText.save')}
        >
          <CheckIcon className="size-5" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={handleCancel}
          aria-label={t('copyWidget.editableText.discard')}
        >
          <XIcon className="size-5" />
        </Button>
      </div>
    </div>
  );
};
